import React, { useState } from 'react';
import PhotoUploader from '../../Photos/PhotoUploader';
import PhotoGallery from '../../Photos/PhotoGallery';
import { usePhotos } from '../../../hooks/usePhotos';

/**
 * Vehicle Photos Modal
 *
 * Standalone modal for managing vehicle photos from the /vehicles/manage page
 * Provides upload and gallery functionality in a modal interface
 */
const VehiclePhotosModal = ({ vehicle, onClose }) => {
  const [activeTab, setActiveTab] = useState('upload');
  const { photos, loading, addPhotos, removePhoto } = usePhotos('vehicle', vehicle.id);

  const handleUploadComplete = async (uploadedPhotos) => {
    await addPhotos(uploadedPhotos);
  };

  const handlePhotosUpdate = async (updatedPhotos) => {
    const removedPhoto = photos.find(
      p => !updatedPhotos.find(up => up.storagePath === p.storagePath)
    );

    if (removedPhoto) {
      await removePhoto(removedPhoto);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
      <div className="flex items-end justify-center min-h-screen pt-0 px-0 pb-0 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div
          className="fixed inset-0 bg-black/30 backdrop-blur-sm transition-opacity"
          aria-hidden="true"
          onClick={onClose}
        ></div>

        {/* Center modal */}
        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

        <div className="inline-block align-bottom bg-white rounded-t-2xl sm:rounded-2xl text-left overflow-hidden shadow-2xl transform transition-all sm:my-8 sm:align-middle sm:max-w-5xl w-full sm:w-full max-h-screen">
          {/* Header */}
          <div className="bg-gradient-to-r from-emerald-500 to-teal-600 px-4 sm:px-6 py-3 sm:py-4">
            <div className="flex items-start sm:items-center justify-between gap-3">
              <div className="flex-1 min-w-0">
                <h3 className="text-base sm:text-xl font-bold text-white truncate" id="modal-title">
                  Vehicle Photos
                </h3>
                <p className="text-xs sm:text-sm text-blue-100 mt-1 truncate">
                  {vehicle.vehicleMake} {vehicle.vehicleModel} • {vehicle.registrationNumber}
                </p>
                <p className="text-xs text-blue-200 mt-1">
                  {photos.length} photo(s) uploaded
                </p>
              </div>
              <button
                onClick={onClose}
                className="text-white hover:text-gray-200 transition-colors flex-shrink-0"
              >
                <svg className="h-5 w-5 sm:h-6 sm:w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Legal Protection Notice */}
          <div className="px-3 sm:px-6 pt-3 sm:pt-4">
            <div className="bg-emerald-50 border-l-4 border-emerald-500 p-3 sm:p-4 rounded-xl">
              <div className="flex gap-2">
                <div className="flex-shrink-0">
                  <svg className="h-4 w-4 sm:h-5 sm:w-5 text-emerald-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-xs sm:text-sm font-bold text-emerald-900">
                    Legal Protection - Vehicle Documentation
                  </h3>
                  <div className="mt-1 sm:mt-2 text-xs sm:text-sm text-emerald-700">
                    <p>
                      Document the vehicle condition to protect against false damage claims.
                      Upload photos from multiple angles and any existing damage.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="px-3 sm:px-6 pt-3 sm:pt-4">
            <div className="border-b border-gray-200">
              <nav className="-mb-px flex space-x-4 sm:space-x-8">
                <button
                  onClick={() => setActiveTab('upload')}
                  className={`${
                    activeTab === 'upload'
                      ? 'border-emerald-500 text-emerald-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  } whitespace-nowrap py-3 sm:py-4 px-1 border-b-2 font-bold text-xs sm:text-sm transition-colors duration-200`}
                >
                  Upload Photos
                </button>
                <button
                  onClick={() => setActiveTab('gallery')}
                  className={`${
                    activeTab === 'gallery'
                      ? 'border-emerald-500 text-emerald-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  } whitespace-nowrap py-3 sm:py-4 px-1 border-b-2 font-bold text-xs sm:text-sm transition-colors duration-200`}
                >
                  View Gallery ({photos.length})
                </button>
              </nav>
            </div>
          </div>

          {/* Tab Content */}
          <div className="px-3 sm:px-6 py-3 sm:py-4 max-h-[50vh] sm:max-h-[60vh] overflow-y-auto">
            {activeTab === 'upload' && (
              <PhotoUploader
                entityType="vehicle"
                entityId={vehicle.id}
                onUploadComplete={handleUploadComplete}
                existingPhotos={photos}
                maxPhotos={20}
                categories={[
                  'Front View',
                  'Rear View',
                  'Left Side',
                  'Right Side',
                  'Dashboard',
                  'Interior - Front',
                  'Interior - Rear',
                  'Engine Bay',
                  'Trunk/Boot',
                  'Existing Damage',
                  'Wheels/Tires',
                  'VIN Plate',
                  'Other'
                ]}
              />
            )}

            {activeTab === 'gallery' && (
              <>
                {loading ? (
                  <div className="text-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Loading photos...</p>
                  </div>
                ) : (
                  <PhotoGallery
                    entityType="vehicle"
                    entityId={vehicle.id}
                    photos={photos}
                    onPhotosUpdate={handlePhotosUpdate}
                    readOnly={false}
                  />
                )}
              </>
            )}
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-3 sm:px-6 py-3 sm:py-4 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 border-t border-gray-200">
            <div className="text-xs sm:text-sm text-gray-700 text-center sm:text-left">
              {photos.length > 0 ? (
                <span>
                  <strong className="font-bold">{photos.length}</strong> photo(s) • Max 20 photos per vehicle
                </span>
              ) : (
                <span className="font-medium">No photos uploaded yet</span>
              )}
            </div>
            <button
              onClick={onClose}
              className="w-full sm:w-auto px-6 py-2.5 bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-xl hover:from-emerald-600 hover:to-teal-700 transition-all duration-200 font-bold text-sm shadow-lg hover:shadow-xl"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VehiclePhotosModal;
